import { useCallback, useEffect, useState } from "react";

import { PIN_STATE_STORAGE_KEY } from "../constants";

/**
 * Reads the persisted pin state, defaulting to pinned (matching Backstage's
 * previous default). Guarded because `localStorage` throws in some privacy
 * modes and is absent during SSR/tests.
 *
 * The result is coerced rather than returned straight from `JSON.parse`. This
 * reads a value a user can edit, and one written by earlier versions of this
 * app: `"null"`, `"0"` and `"{}"` all parse without throwing, and any of them
 * would otherwise be published through `useSidebarPinState()` — which
 * `plugin-search`, `plugin-user-settings` and `plugin-techdocs` all consume —
 * and rendered into `data-pinned`, where an object stringifies to
 * `"[object Object]"` and silently drops the pinned content offset.
 */
function readPinState(): boolean {
    try {
        const stored = window.localStorage.getItem(PIN_STATE_STORAGE_KEY);
        if (stored === null) {
            return true;
        }

        const parsed: unknown = JSON.parse(stored);
        return typeof parsed === "boolean" ? parsed : true;
    } catch (error) {
        console.error(
            `Could not read the sidebar pin state from localStorage ` +
                `("${PIN_STATE_STORAGE_KEY}"); defaulting to pinned.`,
            error
        );
        return true;
    }
}

/** Persists the pin state, ignoring quota/privacy-mode failures. */
function writePinState(isPinned: boolean): void {
    try {
        window.localStorage.setItem(
            PIN_STATE_STORAGE_KEY,
            JSON.stringify(isPinned)
        );
    } catch (error) {
        // Persistence is a convenience; a failure here must not break the app.
        // It is logged rather than swallowed because the visible symptom — the
        // rail forgetting its pin on every reload — gives no hint that
        // `localStorage` was the cause.
        console.error(
            `Could not persist the sidebar pin state to localStorage ` +
                `("${PIN_STATE_STORAGE_KEY}"); the preference will not survive ` +
                `a reload.`,
            error
        );
    }
}

/**
 * Sidebar pin state, restored from `localStorage` on mount and written back on
 * every change.
 *
 * Split out of {@link AppLayout} so the storage handling — lazy read, coercion
 * of untrusted values, and the two failure paths above — is testable and
 * reusable without rendering the whole app shell.
 *
 * @returns The current state and a stable toggle. The toggle's identity does
 * not change between renders, so consumers that pass it into a memoised value
 * or dependency array do not re-run on every parent render.
 */
export function usePersistedPinState(): [boolean, () => void] {
    const [isPinned, setIsPinned] = useState(readPinState);

    useEffect(() => {
        writePinState(isPinned);
    }, [isPinned]);

    const toggleSidebarPinState = useCallback(
        () => setIsPinned(pinned => !pinned),
        []
    );

    return [isPinned, toggleSidebarPinState];
}
